<template>
  <LoadingComponent v-if="isLoadingContact" title="Loading contact"
    message="Looking up contact information for this record." :minHeight="140" />
  <div class="member-card" v-else-if="effectiveContact">
    <div class="member-card__header-row">
      <h5 class="member-card__title">{{ effectiveContact.name || 'Member Contact' }}</h5>
      <button v-if="editable && !isEditing" type="button" class="btn btn-sm btn-outline-primary" @click="startEdit">
        Edit Contact
      </button>
    </div>

    <div v-if="isEditing" class="member-card__form">
      <div class="row g-2">
        <div class="col-12">
          <label class="form-label">Name</label>
          <input v-model="form.name" type="text" class="form-control" />
        </div>
        <div class="col-12">
          <label class="form-label">Email</label>
          <input v-model="form.email" type="email" class="form-control" readonly />
          <small class="text-muted">Email is linked to your account and cannot be edited here.</small>
        </div>
        <div class="col-12">
          <label class="form-label">Phone</label>
          <input v-model="form.phone" type="tel" class="form-control" />
        </div>
        <div class="col-12">
          <label class="form-label">Street Address</label>
          <input v-model="form.street_1" type="text" class="form-control" />
        </div>
        <div class="col-12">
          <label class="form-label">Street Address 2</label>
          <input v-model="form.street_2" type="text" class="form-control" />
        </div>
        <div class="col-md-6">
          <label class="form-label">City</label>
          <input v-model="form.city" type="text" class="form-control" />
        </div>
        <div class="col-md-3">
          <label class="form-label">State</label>
          <input v-model="form.state" type="text" class="form-control" maxlength="2" />
        </div>
        <div class="col-md-3">
          <label class="form-label">Postal Code</label>
          <input v-model="form.postal_code" type="text" class="form-control" />
        </div>
      </div>
      <div class="member-card__actions mt-3">
        <button type="button" class="btn btn-sm btn-primary" :hidden="!editable" :disabled="isSaving" @click="saveEdit">
          {{ isSaving ? 'Saving...' : 'Save Contact' }}
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="isSaving" @click="cancelEdit">
          Cancel
        </button>
      </div>
      <p v-if="errorMessage" class="member-card__error mb-0 mt-2">{{ errorMessage }}</p>
    </div>

    <div v-else>
      <p v-if="effectiveContact.email"><strong>Email:</strong> <a :href="`mailto:${effectiveContact.email}`">{{
        effectiveContact.email }}</a>
      </p>
      <p v-if="effectiveContact.phone"><strong>Phone:</strong> <a :href="`tel:${effectiveContact.phone}`">{{
        effectiveContact.phone }}</a>
      </p>
      <p
        v-if="effectiveContact.street_1 || effectiveContact.city || effectiveContact.state || effectiveContact.postal_code">
        <strong>Address:</strong>
        {{ [effectiveContact.street_1, effectiveContact.street_2].filter(Boolean).join(' ') }}
        {{ [effectiveContact.city, effectiveContact.state, effectiveContact.postal_code].filter(Boolean).join(', ') }}
      </p>
      <p v-if="effectiveContact.status"><strong>Status:</strong> {{ effectiveContact.status }}</p>
      <p v-if="successMessage" class="member-card__success mb-0">{{ successMessage }}</p>
    </div>
  </div>
  <div v-else class="member-card">
    Contact details are not available yet.
  </div>
</template>

<script>
import { mapStores } from 'pinia';
import { useCurrentUserStore } from '@/common/stores/currentUserStore';
import { useDataStore } from '@/common/stores/dataStore';
import LoadingComponent from '@/common/components/LoadingComponent.vue';
import { logAnalyticsEvent } from '@/common/lib/firebase';

export default {
  name: 'ContactCardComponent',
  components: {
    LoadingComponent,
  },
  props: {
    contact: {
      type: Object,
      default: null,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    contactId: {
      type: String,
      default: null,
    },
    firebaseUserId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isEditing: false,
      isSaving: false,
      isLoadingContact: false,
      fallbackContact: null,
      successMessage: '',
      errorMessage: '',
      form: {
        name: '',
        email: '',
        phone: '',
        street_1: '',
        street_2: '',
        city: '',
        state: '',
        postal_code: '',
      },
    };
  },
  computed: {
    ...mapStores(useCurrentUserStore, useDataStore),
    effectiveContact() {
      return this.contact || this.fallbackContact;
    },
    effectiveContactId() {
      return this.contactId || this.effectiveContact?.id || this.effectiveContact?.contact_id || null;
    },
  },
  watch: {
    contact: {
      immediate: true,
      async handler(newContact) {
        if (newContact) {
          this.fallbackContact = null;
          this.setFormFromContact(newContact);
          return;
        }

        await this.loadContactFromStore();
      },
    },
    contactId: {
      immediate: true,
      async handler() {
        if (!this.contact) {
          await this.loadContactFromStore();
        }
      },
    },
    firebaseUserId: {
      immediate: true,
      async handler() {
        if (!this.contact) {
          await this.loadContactFromStore();
        }
      },
    },
  },
  methods: {
    getErrorCode(error) {
      return typeof error?.code === 'string' ? error.code : 'unknown';
    },

    async loadContactFromStore() {
      if (!this.contactId && !this.firebaseUserId) {
        this.isLoadingContact = false;
        this.fallbackContact = null;
        this.setFormFromContact(null);
        return;
      }

      this.isLoadingContact = true;
      try {
        let resolvedContact = null;

        if (this.firebaseUserId) {
          resolvedContact = await this.dataStore.fetchFirebaseContactByUserId(this.firebaseUserId);
        } else {
          const contactsById = await this.dataStore.fetchCountyContactsById();
          resolvedContact = contactsById?.[this.contactId] || contactsById?.[String(this.contactId)] || null;
        }

        this.fallbackContact = resolvedContact;
        this.setFormFromContact(resolvedContact);
      } catch (error) {
        logAnalyticsEvent('contact_card_load_failed', {
          load_path: this.firebaseUserId ? 'firebase_user_id' : 'contact_id',
          error_code: this.getErrorCode(error),
        });
        console.error('Unable to load contact from store:', error);
      } finally {
        this.isLoadingContact = false;
      }
    },

    setFormFromContact(contact) {
      this.form = {
        name: contact?.name || '',
        email: contact?.email || 'unknown',
        phone: contact?.phone || '',
        street_1: contact?.street_1 || '',
        street_2: contact?.street_2 || '',
        city: contact?.city || '',
        state: contact?.state || '',
        postal_code: contact?.postal_code || '',
      };
    },

    startEdit() {
      if (!this.editable) return;
      this.successMessage = '';
      this.errorMessage = '';
      this.isEditing = true;
      logAnalyticsEvent('contact_card_edit_started');
    },

    cancelEdit() {
      this.isEditing = false;
      this.errorMessage = '';
      this.setFormFromContact(this.effectiveContact);
      logAnalyticsEvent('contact_card_edit_cancelled');
    },

    async saveEdit() {
      if (!this.editable) return;
      this.errorMessage = '';
      this.successMessage = '';
      this.isSaving = true;
      const targetContactId = this.effectiveContactId;

      logAnalyticsEvent('contact_card_save_attempted', {
        has_contact_id: Boolean(targetContactId),
      });

      if (!targetContactId) {
        this.errorMessage = 'Unable to save because the contact id is missing.';
        this.isSaving = false;
        logAnalyticsEvent('contact_card_save_failed', {
          error_code: 'missing-contact-id',
        });
        return;
      }


      try {
        const updatedContact = await this.currentUserStore.updateUserContact(targetContactId, this.form);
        if (!updatedContact) {
          this.errorMessage = 'Unable to save your contact updates.';
          logAnalyticsEvent('contact_card_save_failed', {
            error_code: 'empty-update-response',
          });
          return;
        }

        this.setFormFromContact(updatedContact);
        this.fallbackContact = updatedContact;
        this.isEditing = false;
        this.successMessage = 'Contact details updated.';
        logAnalyticsEvent('contact_card_save_succeeded', {
          has_phone: Boolean(updatedContact.phone),
          has_address: Boolean(
            updatedContact.street_1
            || updatedContact.city
            || updatedContact.state
            || updatedContact.postal_code,
          ),
        });
      } catch (error) {
        this.errorMessage = 'Unable to save your contact updates.';
        logAnalyticsEvent('contact_card_save_failed', {
          error_code: this.getErrorCode(error),
        });
        console.error('Unable to save contact updates:', error);
      } finally {
        this.isSaving = false;
      }
    },
  },
}
</script>

<style scoped>
.member-card {
  border: 1px solid #dce4fb;
  border-radius: 10px;
  padding: 1rem;
  background: #fff;
}

.member-card__title {
  margin-bottom: 0.75rem;
  color: var(--blue-dark);
}

.member-card__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.member-card__title {
  margin: 0;
}

.member-card__actions {
  display: flex;
  gap: 0.5rem;
}

.member-card__error {
  color: #b42318;
  font-size: 0.9rem;
}

.member-card__success {
  color: #146c43;
  font-size: 0.9rem;
}

.member-card p {
  margin-bottom: 0.5rem;
}
</style>